import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { accountService } from '../../../services/accountService';
import { formatCurrency } from '../../../utils/currency';

const GoalCard = ({ goal, onEdit, onDelete, onViewDetails, getProgressColor, getProgressBarColor }) => {
  const [linkedAccount, setLinkedAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(false);

  useEffect(() => {
    if (goal?.linkedAccountId) {
      loadLinkedAccount();
    }
  }, [goal?.linkedAccountId]);

  const loadLinkedAccount = async () => {
    try {
      setLoadingAccount(true);
      const account = await accountService?.getById(goal?.linkedAccountId);
      setLinkedAccount(account);
    } catch (err) {
      console.error('Error loading linked account:', err);
    } finally {
      setLoadingAccount(false);
    }
  };

  // Use account balance if linked, otherwise use manual current amount
  const currentAmount = linkedAccount ? linkedAccount?.balance : goal?.currentAmount;
  
  // Calculate progress based on goal type
  let progress = 0;
  let remaining = 0;
  
  if (goal?.goalType === 'debt_payoff') {
    // For debt payoff: progress increases as balance decreases
    // If target is 0, we're aiming to pay off all debt
    // Progress = how much debt has been paid off
    const initialDebt = goal?.targetAmount === 0 ? goal?.currentAmount : Math.max(goal?.currentAmount, currentAmount);
    if (initialDebt > 0) {
      const paidOff = initialDebt - currentAmount;
      progress = (paidOff / initialDebt) * 100;
    }
    remaining = currentAmount - goal?.targetAmount; // How much debt is left to pay
  } else {
    // For savings: progress increases as balance increases
    progress = goal?.targetAmount > 0 
      ? (currentAmount / goal?.targetAmount * 100) 
      : 0;
    remaining = goal?.targetAmount - currentAmount; // How much more to save
  }
  
  const getGoalTypeIcon = (type) => {
    return type === 'savings_target' ? 'PiggyBank' : 'CreditCard';
  };

  const getGoalTypeColor = (type) => {
    return type === 'savings_target' ? 'var(--color-primary)' : 'var(--color-destructive)';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Active', color: 'bg-success/10 text-success' },
      completed: { label: 'Completed', color: 'bg-primary/10 text-primary' },
      paused: { label: 'Paused', color: 'bg-muted text-muted-foreground' }
    };
    return badges?.[status] || badges?.active;
  };

  const statusBadge = getStatusBadge(goal?.status);

  return (
    <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth">
      <div className="flex items-start justify-between mb-16">
        <div className="flex items-center flex-1 min-w-0">
          <div 
            className="flex items-center justify-center w-12 h-12 rounded-lg flex-shrink-0"
            style={{ backgroundColor: `${getGoalTypeColor(goal?.goalType)}15` }}
          >
            <Icon 
              name={getGoalTypeIcon(goal?.goalType)} 
              size={24} 
              color={getGoalTypeColor(goal?.goalType)} 
              strokeWidth={2.5} 
            />
          </div>
          <div className="ml-12 flex-1 min-w-0">
            <h3 className="text-lg font-heading font-semibold text-card-foreground truncate">
              {goal?.goalName}
            </h3>
            <div className="flex items-center gap-8 mt-4">
              <span className={`inline-block px-8 py-4 rounded text-xs font-medium ${statusBadge?.color}`}>
                {statusBadge?.label}
              </span>
              {goal?.autoCloseOnCompletion && (
                <span className="inline-flex items-center gap-4 px-8 py-4 rounded text-xs font-medium bg-accent/10 text-accent">
                  <Icon name="CheckCircle2" size={12} />
                  Auto-close
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-8 ml-12">
          <button
            onClick={onEdit}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[36px] min-w-[36px] flex items-center justify-center"
            aria-label="Edit goal"
          >
            <Icon name="Edit2" size={16} color="var(--color-muted-foreground)" />
          </button>
          <button
            onClick={onDelete}
            className="p-8 hover:bg-destructive/10 rounded-lg transition-smooth min-h-[36px] min-w-[36px] flex items-center justify-center"
            aria-label="Delete goal"
          >
            <Icon name="Trash2" size={16} color="var(--color-destructive)" />
          </button>
        </div>
      </div>

      {linkedAccount && (
        <div className="mb-16 p-12 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-8">
            <Icon name="Link" size={14} color="var(--color-primary)" />
            <span className="text-xs font-medium text-primary">Linked to Account</span>
          </div>
          <p className="text-sm text-card-foreground mt-4">
            {linkedAccount?.name} ({linkedAccount?.group})
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Progress tracked automatically from account balance
          </p>
        </div>
      )}

      <div className="mb-16">
        <div className="flex items-center justify-between mb-8">
          <span className="text-sm text-muted-foreground">Progress</span>
          <span className={`text-sm font-semibold ${getProgressColor(progress)}`}>
            {progress?.toFixed(1)}%
          </span>
        </div>
        <div className="w-full h-8 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-smooth ${getProgressBarColor(progress)}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      <div className="space-y-12 mb-20">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {linkedAccount ? 'Account Balance' : 'Current'}
          </span>
          <span className="text-base font-heading font-semibold text-card-foreground">
            {formatCurrency(currentAmount)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Target</span>
          <span className="text-base font-heading font-semibold text-card-foreground">
            {formatCurrency(goal?.targetAmount)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {goal?.goalType === 'debt_payoff' ? 'Remaining Debt' : 'Remaining'}
          </span>
          <span className="text-base font-heading font-semibold text-accent">
            {formatCurrency(Math.max(0, remaining))}
          </span>
        </div>
        {goal?.deadline && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Deadline</span>
            <span className="text-sm font-medium text-card-foreground">
              {new Date(goal?.deadline)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
          </div>
        )}
      </div>

      <button
        onClick={onViewDetails}
        className="w-full px-16 py-12 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-smooth font-medium min-h-[44px] flex items-center justify-center gap-8"
      >
        View Details
        <Icon name="ArrowRight" size={16} color="currentColor" />
      </button>
    </div>
  );
};

export default GoalCard;